import React from 'react';

/**
 * Modal de Confirmação Profissional
 * 
 * Props:
 * - title: Título do modal
 * - message: Mensagem de confirmação
 * - confirmText: Texto do botão de confirmação (padrão: "Confirmar")
 * - cancelText: Texto do botão de cancelar (padrão: "Cancelar")
 * - confirmType: Tipo visual do botão (danger, warning, success) (padrão: "danger")
 * - onConfirm: Callback quando confirmar
 * - onCancel: Callback quando cancelar
 */
const ConfirmModal = ({ 
  title = "Confirmar ação",
  message, 
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  confirmType = "danger",
  onConfirm, 
  onCancel 
}) => {
  const getConfirmButtonStyle = () => {
    const baseStyle = {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      minWidth: '120px',
      transition: 'all 0.2s',
      color: '#fff'
    };

    const typeStyles = {
      danger: {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
      },
      warning: {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
      },
      success: {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
      }
    };

    return { ...baseStyle, ...typeStyles[confirmType] };
  };

  return (
    <>
      <style>
        {`
          .confirm-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.6);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 10001;
            animation: fadeIn 0.2s ease-out;
          }

          .confirm-modal-content {
            background: white;
            border-radius: 16px;
            padding: 24px;
            max-width: 450px;
            width: 90%;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
            animation: slideUp 0.3s ease-out;
          }

          .confirm-modal-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 16px;
          }

          .confirm-modal-icon {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            flex-shrink: 0;
          }

          .confirm-modal-icon.danger {
            background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
            color: #dc2626;
          }

          .confirm-modal-icon.warning {
            background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
            color: #d97706;
          }

          .confirm-modal-icon.success {
            background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
            color: #059669;
          }

          .confirm-modal-title {
            font-size: 20px;
            font-weight: 700;
            color: #1f2937;
            margin: 0;
            flex: 1;
          }

          .confirm-modal-message {
            font-size: 15px;
            line-height: 1.6;
            color: #6b7280;
            margin-bottom: 24px;
          }

          .confirm-modal-buttons {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
          }

          .confirm-modal-btn-cancel {
            padding: 12px 24px;
            border-radius: 8px;
            border: 2px solid #e5e7eb;
            background: white;
            color: #6b7280;
            fontSize: 15px;
            fontWeight: 600;
            cursor: pointer;
            minWidth: 120px;
            transition: all 0.2s;
          }

          .confirm-modal-btn-cancel:hover {
            background: #f9fafb;
            border-color: #d1d5db;
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideUp {
            from {
              transform: translateY(20px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }

          @media (max-width: 768px) {
            .confirm-modal-content {
              width: 85%;
              padding: 20px;
            }

            .confirm-modal-buttons {
              flex-direction: column-reverse;
            }

            .confirm-modal-btn-cancel,
            .confirm-modal-buttons button {
              width: 100%;
            }
          }
        `}
      </style>
      
      <div className="confirm-modal-overlay" onClick={onCancel}>
        <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="confirm-modal-header">
            <div className={`confirm-modal-icon ${confirmType}`}>
              {confirmType === 'danger' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              )}
              {confirmType === 'warning' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              )}
              {confirmType === 'success' && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              )}
            </div>
            <h3 className="confirm-modal-title">{title}</h3>
          </div>
          
          <p className="confirm-modal-message">{message}</p>
          
          <div className="confirm-modal-buttons">
            <button 
              className="confirm-modal-btn-cancel"
              onClick={onCancel}
            >
              {cancelText}
            </button>
            <button 
              style={getConfirmButtonStyle()}
              onClick={onConfirm}
              onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmModal;
